import React, { useEffect, useState } from 'react';
import { tenantAdminApi } from '../../utils/tenantAdminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Globe, Plus, Trash2, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function DomainsPage() {
  const [domains, setDomains] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slug, setSlug] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const [d, t] = await Promise.all([tenantAdminApi.domains(), tenantAdminApi.tenant()]);
      setDomains(d.allowed_domains || []);
      setSlug(t.slug);
    } catch (_) {}
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const add = async () => {
    const d = input.trim().toLowerCase();
    if (!d) return;
    setSaving(true);
    try {
      await tenantAdminApi.addDomain(d);
      setInput('');
      toast.success('Domain added');
      refresh();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Failed');
    }
    setSaving(false);
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove ${d}?`)) return;
    try {
      await tenantAdminApi.removeDomain(d);
      toast.success('Removed');
      refresh();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="p-8 max-w-3xl">
      <h1
        className="text-2xl font-bold text-zinc-900"
        style={{ fontFamily: 'Manrope' }}
        data-testid="ta-domains-title"
      >
        Custom domains
      </h1>
      <p className="text-sm text-zinc-500 mt-1">
        Use your own domain to access this workspace. Visitors landing on a
        mapped domain are automatically routed to your tenant.
      </p>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-5 text-sm space-y-2">
        <div className="font-semibold text-blue-900 flex items-center gap-2">
          <Globe className="h-4 w-4" /> Default subdomain
        </div>
        <p className="text-blue-900/80">
          Your workspace is always reachable at:
        </p>
        <div className="font-mono bg-white border border-blue-200 rounded px-3 py-2 text-zinc-900 select-all" data-testid="ta-domain-default">
          {slug ? `${slug}.<your-domain>` : '<your-tenant-slug>.<your-domain>'}
        </div>
        <p className="text-xs text-blue-900/70">
          Configure a wildcard <span className="font-mono">CNAME *.your-domain → app.your-domain</span>{' '}
          on your DNS provider; the backend already auto-routes by subdomain.
        </p>
      </div>

      <div className="mt-6 bg-white border rounded-xl p-5">
        <h3 className="font-semibold text-zinc-900 mb-3">Custom domains</h3>
        <div className="flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="emr.your-clinic.com"
            onKeyDown={(e) => e.key === 'Enter' && add()}
            data-testid="ta-domain-input" />
          <Button onClick={add} disabled={saving || !input.trim()}
            className="bg-blue-700 hover:bg-blue-800" data-testid="ta-domain-add">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-1" />}
            Add
          </Button>
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          Point a <span className="font-mono">CNAME</span> from your domain
          to your eCare 360 host, then add it here. Requests with that
          <span className="font-mono"> Host</span> header are auto-routed to
          this workspace.
        </p>

        <div className="mt-5 space-y-2">
          {loading ? (
            <p className="text-sm text-zinc-500">Loading…</p>
          ) : domains.length === 0 ? (
            <p className="text-sm text-zinc-500">No custom domains yet.</p>
          ) : domains.map((d) => (
            <div key={d}
              className="flex items-center justify-between border rounded-lg px-3 py-2"
              data-testid={`ta-domain-row-${d}`}>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span className="font-mono text-sm">{d}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => remove(d)}
                className="text-red-700 border-red-200 hover:bg-red-50"
                data-testid={`ta-domain-remove-${d}`}>
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
